import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { RolePill, useEffectiveRole } from "../lib/view-as-context";
import { HouseModernIcon, RolodexIcon } from "./icons";
import { PropertySwitcher } from "./PropertySwitcher";
import { UserMenu } from "./UserMenu";
import { PropertySettingsDialog } from "./PropertySettingsDialog";

type Role = "landlord" | "tenant" | null | undefined;

type PropertyRow = {
  _id: string;
  slug: string;
  name: string;
  address: string;
  timezone: string;
};

// Single header shared by every property-scoped route (Dashboard,
// Tenants, Contractors). The same nav items render on every page; the
// active route gets a slate-100 background so the user always knows
// where they are without losing access to the rest of the nav.
export function AppHeader() {
  const property = useProperty();
  const location = useLocation();
  const realRole = useQuery(
    anyApi.memberships.getMyRoleOnProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as Role;
  const role = useEffectiveRole(realRole);
  const propertyRow = useQuery(
    anyApi.properties.getBySlug,
    property ? { slug: property.slug } : "skip",
  ) as PropertyRow | null | undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);

  const slug = property?.slug;
  const path = location.pathname;
  const onDashboard = !!slug && path === `/${slug}/dashboard`;
  const onTenants = !!slug && path === `/${slug}/tenants`;
  const onContractors = !!slug && path === `/${slug}/contractors`;

  return (
    <>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HouseModernIcon size={32} className="text-slate-900 shrink-0" />
            <div className="flex items-center gap-2">
              <PropertySwitcher
                currentSlug={property?.slug}
                currentName={propertyRow?.name ?? property?.name ?? "maisons"}
                currentAddress={
                  propertyRow?.address ?? property?.address ?? ""
                }
              />
              <RolePill realRole={realRole} />
            </div>
          </div>
          <nav className="flex items-center gap-1.5">
            {slug && (
              <NavLink to={`/${slug}/dashboard`} active={onDashboard}>
                Dashboard
              </NavLink>
            )}
            {role === "landlord" && slug && (
              <>
                <NavLink to={`/${slug}/tenants`} active={onTenants}>
                  Tenants
                </NavLink>
                <NavLink to={`/${slug}/contractors`} active={onContractors}>
                  <RolodexIcon />
                  Rolodex
                </NavLink>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                >
                  Settings
                </button>
              </>
            )}
            <div className="ml-2">
              <UserMenu />
            </div>
          </nav>
        </div>
      </header>
      {propertyRow && (
        <PropertySettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          property={propertyRow}
        />
      )}
    </>
  );
}

function NavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-slate-100 text-slate-900"
          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
      }`}
    >
      {children}
    </Link>
  );
}
